import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LENSES, NODE_CLASS, PERSONA_QUESTION, RISK_CLASS, RISK_COLOR, RISK_GROUPS, RISK_LABEL, CATEGORY_LABEL } from "@/lib/labels";

const categoryLabel = (key) => CATEGORY_LABEL[key] || key;
const GROUP = Object.fromEntries(RISK_GROUPS.map((g) => [g.id, g]));
const groupLabel = (key) => GROUP[key]?.label || key;

/**
 * The radar opens on one bubble per risk group — what the cases put at risk,
 * not the channel they came in through, with feature requests and no-risk
 * cases together under Low risk — so a heavy inflow reads as a few groups
 * rather than hundreds of dots. A bubble's area is proportional to
 * its case count. Clicking a group plots its cases; the group lives in the
 * URL, so returning from a case lands back on the same drill-in.
 */
export default function RadarPage() {
  const { persona } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const group = params.get("group");
  const [lens, setLens] = useState("executive");
  const [data, setData] = useState({ nodes: [], groups: [] });
  const [hover, setHover] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const scale = useCanvasScale(canvasRef);

  useEffect(() => {
    setLoading(true);
    const query = group ? `&group=${encodeURIComponent(group)}` : "";
    api.get(`/radar?lens=${lens}${query}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [lens, group]);

  const openGroup = (key) => {
    setHover(null);
    setParams(key ? { group: key } : {});
  };
  const chooseLens = (id) => {
    // A group from one lens may hold nothing in the next; start from the overview.
    setLens(id);
    openGroup(null);
  };

  const groups = useMemo(() => data.groups || [], [data]);
  const nodes = useMemo(() => data.nodes || [], [data]);
  const selected = group ? groups.find((g) => g.group === group) : null;

  // build deterministic angle per case id
  const placed = useMemo(() => {
    const golden = 137.508;
    return nodes.map((n, i) => {
      const dist = 1 - Math.min(100, Math.max(0, n.attention_score)) / 100; // 0=center
      const radius = 60 + dist * 240; // px from center
      const angle = (i * golden) % 360;
      const size = 12 + Math.round((n.business_impact_score / 100) * 24); // 12..36px
      return { ...n, _x: angle, _r: radius, _size: size };
    });
  }, [nodes]);

  const bubbles = useMemo(() => layoutGroups(groups), [groups]);
  // Counted server-side over every case in scope, not just the plotted ones.
  const byGroup = useMemo(() => Object.fromEntries(groups.map((g) => [g.group, g])), [groups]);

  const total = data.total ?? nodes.length;
  const nodeTotal = data.node_total ?? nodes.length;

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-4 lg:gap-6 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--text-muted)" }}>Decision Radar™</div>
          <h1 className="font-display text-[28px] md:text-[32px] leading-tight max-w-[640px]" data-testid="radar-question">
            {PERSONA_QUESTION[persona] || PERSONA_QUESTION.founder}
          </h1>
          <p className="text-[13px] mt-2" style={{ color: "var(--text-secondary)" }} data-testid="radar-subtitle">
            {group
              ? <>{nodeTotal} {groupLabel(group)} case{nodeTotal === 1 ? "" : "s"} · ranked by attention score · click any node to open the Case Workspace
                  {nodeTotal > nodes.length && <> · showing the top {nodes.length}</>}</>
              : <>{total} live case{total === 1 ? "" : "s"} by risk · bigger means more cases, nearer the center more urgent · click a group to see its cases</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 max-w-[520px] lg:justify-end">
          {LENSES.map((l) => (
            <button
              key={l.id}
              data-testid={`lens-${l.id}`}
              onClick={() => chooseLens(l.id)}
              className={`px-3 py-1.5 rounded-md text-[12px] border transition ${
                lens === l.id ? "chip-selected" : ""
              }`}
              style={lens === l.id ? {} : { background: "var(--surface)", borderColor: "var(--border)", color: "var(--text-secondary)" }}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Radar canvas */}
        <div className="col-span-12 lg:col-span-8">
          <div ref={canvasRef} className="card relative overflow-hidden" style={{ height: Math.round(620 * scale) }} data-testid="radar-canvas">
            {/* radial rings */}
            {[120, 240, 360, 480, 600].map((s) => (
              <div key={s} className="radar-ring" style={{ width: s * scale, height: s * scale }} />
            ))}
            <div className="radar-cross" style={{ width: 1, height: "100%", top: 0, left: "50%", transform: "translateX(-50%)" }} />
            <div className="radar-cross" style={{ width: "100%", height: 1, top: "50%", left: 0, transform: "translateY(-50%)" }} />
            <div className="radar-sweep" />
            {/* center label */}
            <div className="absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-center" style={{ color: "var(--text-muted)" }}>
                attention<br/>center
              </div>
            </div>

            {group && (
              <div className="absolute left-3 top-3 z-10 flex items-center gap-2 text-[12px]" data-testid="radar-breadcrumb">
                <button type="button" onClick={() => openGroup(null)} data-testid="radar-back"
                        className="flex items-center gap-1 px-2 py-1 rounded-md border hover-wash-subtle"
                        style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                  <ChevronLeft size={13} /> All risk groups
                </button>
                <span className={`badge ${GROUP[group]?.badge || "b-muted"}`}>{groupLabel(group)}</span>
              </div>
            )}

            {!loading && (group ? placed.length === 0 : bubbles.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center text-[13px]" style={{ color: "var(--text-muted)" }}>
                {group ? "No open cases in this group." : "No cases match this lens."}
              </div>
            )}

            {/* overview: one bubble per risk group */}
            {!group && bubbles.map((b) => (
              <button
                key={b.group}
                data-testid={`radar-group-${b.group}`}
                className={`radar-node ${GROUP[b.group]?.node || "radar-node-healthy"}`}
                style={{
                  width: b.size * scale, height: b.size * scale,
                  top: `calc(50% + ${b.y * scale}px)`, left: `calc(50% + ${b.x * scale}px)`,
                  transform: "translate(-50%, -50%)",
                }}
                onMouseEnter={() => setHover({ type: "group", ...b })}
                onMouseLeave={() => setHover((v) => (v?.group === b.group ? null : v))}
                onFocus={() => setHover({ type: "group", ...b })}
                onBlur={() => setHover(null)}
                onClick={() => openGroup(b.group)}
                aria-label={`${groupLabel(b.group)}: ${b.count} cases, highest attention ${b.max_attention}`}
              >
                <span className="font-mono leading-none" style={{ fontSize: Math.max(11, Math.round(b.size * scale * 0.28)) }}>
                  {b.count}
                </span>
                <span className="absolute whitespace-nowrap font-sans text-[11px] leading-none"
                      style={{ top: "100%", marginTop: 5, color: "var(--text-secondary)" }}>
                  {groupLabel(b.group)}
                </span>
              </button>
            ))}

            {/* drill-in: the group's cases */}
            {group && placed.map((n) => {
              const rad = (n._x * Math.PI) / 180;
              const x = Math.cos(rad) * n._r * scale;
              const y = Math.sin(rad) * n._r * scale;
              return (
                <button
                  key={n.id}
                  data-testid={`radar-node-${n.case_ref}`}
                  className={`radar-node ${NODE_CLASS[n.risk_type] || "radar-node-healthy"}`}
                  style={{ width: n._size, height: n._size, top: `calc(50% + ${y}px)`, left: `calc(50% + ${x}px)`, transform: "translate(-50%, -50%)" }}
                  onMouseEnter={() => setHover({ type: "case", ...n })}
                  onMouseLeave={() => setHover((v) => (v?.id === n.id ? null : v))}
                  onClick={() => nav(`/cases/${n.id}`)}
                  title={`${n.case_ref} · ${n.title}`}
                >
                  {n.attention_score}
                  <SecondaryRings node={n} />
                </button>
              );
            })}

            {/* hover tooltip */}
            {hover?.type === "case" && (
              <div className="absolute right-4 top-4 w-[300px] max-w-[calc(100%-2rem)] p-4 rounded-md text-[12.5px] pointer-events-none"
                   style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
                   data-testid="radar-hover-card">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{hover.case_ref}</span>
                  <span className={`badge ${RISK_CLASS[hover.risk_type]}`}>{RISK_LABEL[hover.risk_type]}</span>
                </div>
                <div className="font-medium leading-snug mb-1.5">{hover.title}</div>
                <div className="text-[11.5px] mb-2" style={{ color: "var(--text-secondary)" }}>
                  {hover.customer} · {categoryLabel(hover.category)}
                </div>
                <ClassWeights node={hover} />
                {hover.summary && <p className="text-[11.5px] leading-snug" style={{ color: "var(--text-secondary)" }}>{hover.summary}</p>}
                {hover.decision_title && (
                  <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                    <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: "var(--text-muted)" }}>Recommended decision</div>
                    <div className="text-[12px]">{hover.decision_title}</div>
                  </div>
                )}
              </div>
            )}
            {hover?.type === "group" && (
              <div className="absolute right-4 top-4 w-[260px] max-w-[calc(100%-2rem)] p-4 rounded-md text-[12.5px] pointer-events-none"
                   style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
                   data-testid="radar-group-card">
                <span className={`badge ${GROUP[hover.group]?.badge || "b-muted"}`}>{groupLabel(hover.group)}</span>
                <div className="text-[11.5px] mt-2 mb-2.5" style={{ color: "var(--text-secondary)" }}>
                  {/* Cases filed elsewhere can be involved here too: "9 primary · 14 involved". */}
                  {hover.involved > hover.count
                    ? `${hover.count} primary · ${hover.involved} involved`
                    : `${hover.count} case${hover.count === 1 ? "" : "s"}`}
                  {" "}· highest attention {hover.max_attention}
                </div>
                {GROUP[hover.group]?.types.length > 1 && (
                  <div className="mb-2"><SubtypeBreakdown riskCounts={hover.risk_counts} types={GROUP[hover.group].types} /></div>
                )}
                <CategoryBreakdown categoryCounts={hover.category_counts} limit={3} />
                <div className="text-[10.5px] mt-2.5" style={{ color: "var(--text-muted)" }}>Click to see these cases</div>
              </div>
            )}
          </div>
        </div>

        {/* Side: risk groups + what's in one + top queue */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
          <div className="card p-5">
            <div className="text-[11px] uppercase tracking-[0.16em] mb-3" style={{ color: "var(--text-muted)" }}>Risk distribution</div>
            <div className="space-y-1">
              {RISK_GROUPS.map((g) => {
                const b = byGroup[g.id];
                return (
                  <div key={g.id}>
                    <button type="button" data-testid={`risk-count-${g.id}`}
                            disabled={!b}
                            onClick={() => openGroup(group === g.id ? null : g.id)}
                            aria-pressed={group === g.id}
                            className="w-full flex items-center justify-between px-2 py-1.5 -mx-2 rounded-md hover-wash-subtle transition disabled:opacity-50 disabled:cursor-default"
                            style={group === g.id ? { background: "var(--surface-2)" } : {}}>
                      <span className={`badge ${g.badge}`}>{g.label}</span>
                      <span className="font-mono text-[14px]">{b?.count || 0}</span>
                    </button>
                    {/* A parent group lists what it holds, so "Low risk" is never a mystery. */}
                    {g.types.length > 1 && (
                      <div className="pl-3 pr-0 mt-0.5 mb-1">
                        <SubtypeBreakdown riskCounts={b?.risk_counts} types={g.types} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selected && (
            <div className="card p-5" data-testid="radar-group-breakdown">
              <div className="text-[11px] uppercase tracking-[0.16em] mb-3" style={{ color: "var(--text-muted)" }}>
                What's in {groupLabel(selected.group)}
              </div>
              <CategoryBreakdown categoryCounts={selected.category_counts} />
            </div>
          )}

          <div className="card p-5 flex-1 min-h-0">
            <div className="text-[11px] uppercase tracking-[0.16em] mb-3" style={{ color: "var(--text-muted)" }}>
              Top of queue{selected ? ` · ${groupLabel(selected.group)}` : ""}
            </div>
            <div className="space-y-1.5">
              {placed.slice(0, 6).map((n) => (
                <button
                  key={n.id}
                  data-testid={`top-queue-${n.case_ref}`}
                  onClick={() => nav(`/cases/${n.id}`)}
                  className="w-full text-left p-2.5 rounded-md hover-wash-subtle transition"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>{n.case_ref}</span>
                    <span className="font-mono text-[10.5px]" style={{ color: "var(--text-secondary)" }}>{n.attention_score}</span>
                  </div>
                  <div className="text-[12.5px] leading-snug line-clamp-2">{n.title}</div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className={`badge ${RISK_CLASS[n.risk_type]}`} style={{ fontSize: 10 }}>{RISK_LABEL[n.risk_type]}</span>
                    <span className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>{n.customer}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The classes a case is involved in besides the one it is filed under. */
const secondaryClasses = (n) => (n.risk_classes || []).filter((c) => c !== n.risk_type && RISK_COLOR[c]);

/**
 * One ring per secondary class round a case node, innermost the heaviest;
 * fainter the weaker its weight, so "a little SLA pressure" reads as less
 * than "breached".
 */
const SecondaryRings = ({ node }) =>
  secondaryClasses(node).map((c, i) => (
    <span key={c} aria-hidden="true" data-testid={`radar-ring-${c}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: -(3 + i * 4),
            border: `2px solid ${RISK_COLOR[c]}`,
            opacity: 0.35 + 0.65 * ((node.risk_weights?.[c] ?? 50) / 100),
          }} />
  ));

/** Every class the case is involved in and how strongly, primary first. */
const ClassWeights = ({ node }) => {
  const classes = node.risk_classes || [];
  if (classes.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-2" data-testid="radar-class-weights">
      {classes.map((c) => (
        <span key={c} className={`badge ${RISK_CLASS[c]}`} style={{ fontSize: 10 }}>
          {RISK_LABEL[c]} {node.risk_weights?.[c] ?? ""}
        </span>
      ))}
    </div>
  );
};

/** The risk types inside a parent group, e.g. Low risk's two. */
const SubtypeBreakdown = ({ riskCounts = {}, types }) => (
  <div className="space-y-0.5" data-testid="risk-subtypes">
    {types.map((t) => (
      <div key={t} className="flex items-center justify-between text-[11.5px]" style={{ color: "var(--text-secondary)" }}>
        <span>└ {RISK_LABEL[t]}</span>
        <span className="font-mono">{riskCounts?.[t] || 0}</span>
      </div>
    ))}
  </div>
);

/** Which kinds of escalation make up a risk group, most common first. */
const CategoryBreakdown = ({ categoryCounts = {}, limit }) => {
  const rows = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);
  const shown = limit ? rows.slice(0, limit) : rows;
  const rest = rows.length - shown.length;
  return (
    <div className="space-y-1">
      {shown.map(([k, n]) => (
        <div key={k} className="flex items-center justify-between text-[11.5px]">
          <span style={{ color: "var(--text-secondary)" }}>{categoryLabel(k)}</span>
          <span className="font-mono">{n}</span>
        </div>
      ))}
      {rest > 0 && <div className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>+{rest} more kind{rest === 1 ? "" : "s"}</div>}
    </div>
  );
};

// Bubble diameters span this range; area is proportional to case count, so
// a group with four times the cases looks four times as big, not twice.
const MIN_BUBBLE = 30;
const MAX_BUBBLE = 120;

/**
 * Place risk-group bubbles in the 600px field: nearer the centre for a more
 * urgent top case, area by case count, spread evenly round the dial and then
 * nudged apart so none overlap. Deterministic, so the picture is stable
 * between visits.
 */
function layoutGroups(groups) {
  const maxCount = Math.max(1, ...groups.map((g) => g.count));
  const n = groups.length;
  const pts = groups.map((g, i) => {
    const dist = 1 - Math.min(100, Math.max(0, g.max_attention)) / 100;
    const radius = n === 1 ? 0 : 90 + dist * 170;
    const angle = ((i * 360) / Math.max(1, n) - 90) * (Math.PI / 180);
    const size = Math.max(MIN_BUBBLE, Math.round(MAX_BUBBLE * Math.sqrt(g.count / maxCount)));
    return { ...g, x: Math.cos(angle) * radius, y: Math.sin(angle) * radius, size };
  });
  const LABEL_GAP = 22; // room for the label under each bubble
  for (let iter = 0; iter < 120; iter += 1) {
    let moved = false;
    for (let i = 0; i < pts.length; i += 1) {
      for (let j = i + 1; j < pts.length; j += 1) {
        const a = pts[i];
        const b = pts[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        const min = (a.size + b.size) / 2 + LABEL_GAP;
        if (d >= min) continue;
        if (d === 0) { dx = 1; dy = 0; d = 1; }
        const push = (min - d) / 2;
        a.x -= (dx / d) * push; a.y -= (dy / d) * push;
        b.x += (dx / d) * push; b.y += (dy / d) * push;
        moved = true;
      }
    }
    pts.forEach((p) => {
      const limit = 300 - p.size / 2 - LABEL_GAP;
      const r = Math.hypot(p.x, p.y);
      if (r > limit) { p.x *= limit / r; p.y *= limit / r; }
    });
    if (!moved) break;
  }
  return pts;
}

/**
 * The radar is laid out for a 600px-wide field (outer ring and furthest node
 * at 300px from the centre). On narrower cards this shrinks the whole field
 * so nodes stay inside rather than being clipped at the edges.
 */
function useCanvasScale(ref) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setScale(Math.min(1, (entry.contentRect.width - 40) / 600));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return scale;
}
